import type React from "react";

import { ExternalLink } from "lucide-react";

import type { ScoreEvidence, ScoreExplanation } from "../../types/scoreExplanation";
import {
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";

interface ScoreExplanationDialogProps {
  readonly explanation: ScoreExplanation;
  readonly onOpportunityAction?: (actionRef: string) => void;
}

interface FactorSectionProps {
  readonly heading: string;
  readonly factors: ScoreExplanation["helping"];
}

function FactorSection({ heading, factors }: FactorSectionProps): React.ReactElement | null {
  if (factors.length === 0) {
    return null;
  }
  return (
    <section aria-labelledby={`${heading}-heading`}>
      <h3 id={`${heading}-heading`} className="font-bold text-on-surface">
        {heading}
      </h3>
      <ul className="mt-2 space-y-2 text-sm text-on-surface-variant">
        {factors.map((factor) => (
          <li key={factor.id}>
            <strong className="text-on-surface">{factor.title}.</strong> {factor.detail}
          </li>
        ))}
      </ul>
    </section>
  );
}

function EvidenceItem({ evidence }: { readonly evidence: ScoreEvidence }): React.ReactElement {
  const content = (
    <>
      {evidence.source_label ?? "Report observation"}: {evidence.summary}
    </>
  );
  return (
    <li className="text-sm text-on-surface-variant">
      {evidence.source_url ? (
        <a
          className="inline-flex items-center gap-1 text-primary hover:underline"
          href={evidence.source_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {content}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        content
      )}
    </li>
  );
}

/** Renders an accessible, report-agnostic explanation in a fixed information order. */
export function ScoreExplanationDialog({
  explanation,
  onOpportunityAction,
}: ScoreExplanationDialogProps): React.ReactElement {
  const actionRef = explanation.opportunity?.action_ref;
  return (
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{explanation.score_label}</DialogTitle>
        <DialogDescription>
          {explanation.score_value} out of {explanation.score_max}
          {explanation.score_band ? ` · ${explanation.score_band}` : ""}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-5">
        <section aria-labelledby="reason-heading">
          <h3 id="reason-heading" className="font-bold text-on-surface">
            Reason
          </h3>
          <p className="mt-2 text-sm text-on-surface-variant">{explanation.reason}</p>
        </section>
        <FactorSection heading="Helping you" factors={explanation.helping} />
        <FactorSection heading="Hurting you" factors={explanation.hurting} />
        {explanation.evidence.length > 0 && (
          <section aria-labelledby="evidence-heading">
            <h3 id="evidence-heading" className="font-bold text-on-surface">
              Evidence
            </h3>
            <ul className="mt-2 space-y-2">
              <>
                {explanation.evidence.map((evidence) => (
                  <EvidenceItem key={evidence.id} evidence={evidence} />
                ))}
              </>
            </ul>
          </section>
        )}
        {explanation.opportunity && (
          <section aria-labelledby="opportunity-heading">
            <h3 id="opportunity-heading" className="font-bold text-on-surface">
              Opportunity
            </h3>
            <p className="mt-2 text-sm text-on-surface-variant">
              <strong className="text-on-surface">{explanation.opportunity.title}.</strong>{" "}
              {explanation.opportunity.rationale}
            </p>
            {actionRef && onOpportunityAction && (
              <button
                type="button"
                className="mt-3 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-on-primary"
                onClick={() => onOpportunityAction(actionRef)}
              >
                {explanation.opportunity.action_label ?? "View details"}
              </button>
            )}
          </section>
        )}
        {explanation.caveat && (
          <p className="rounded-lg bg-surface-container-high p-3 text-sm text-on-surface-variant">
            {explanation.caveat}
          </p>
        )}
      </DialogBody>
    </DialogContent>
  );
}
